import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CircleCheck, Trash2, SortAsc, Users, AlertCircle, Menu } from "lucide-react";
import { toast, Toaster } from "react-hot-toast";
import Sidebar from "../Components/Sidebar";
import TopBar from "../Components/TopBar";
import BottomBar from "../Components/BottomBar";
import { getAllElections, deleteElections, startElection, endElection } from "../services/ElectionService";
import ElectionList from "../Components/ElectionList";

const Elections = () => {
    const [elections, setElections] = useState([]);
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectedElections, setSelectedElections] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchElections();

        // Handle responsive sidebar on window resize
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 768);
        };

        window.addEventListener('resize', handleResize);

        // Listen for sidebar toggle events from Sidebar component
        const handleSidebarToggle = (event) => {
            setIsSidebarOpen(event.detail.expanded);
        };

        window.addEventListener('sidebarToggle', handleSidebarToggle);

        // Cleanup listeners
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('sidebarToggle', handleSidebarToggle);
        };
    }, []);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
        // Dispatch the custom event to sync with Sidebar component
        window.dispatchEvent(
            new CustomEvent("sidebarToggle", { detail: { expanded: !isSidebarOpen } })
        );
    };

    const fetchElections = async () => {
        setIsLoading(true);
        try {
            const fetchedElections = await getAllElections();
            setElections(fetchedElections || []);
            toast.success("Election details loaded successfully.");
        } catch (error) {
            console.error("Error fetching elections:", error);
            toast.error("Failed to load election details.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteSelectedDB = async () => {
        if (selectedElections.length === 0) {
            toast.error("Please select elections to delete.");
            return;
        }
        try {
            await deleteElections(selectedElections);
            toast.success("Selected elections deleted successfully.");
            fetchElections();
            setSelectedElections([]);
            setIsSelecting(false);
            setSelectAll(false);
        } catch (error) {
            console.error("Error deleting elections:", error);
            toast.error("Failed to delete elections.");
        }
    };

    const handleDoubleClick = (election) => {
        if (!isSelecting) {
            navigate("/RegisterElectionPage?edit=true", { state: election });
        }
    };

    const handleSelectClick = () => {
        setIsSelecting(!isSelecting);
        setSelectedElections([]);
        setSelectAll(false);
    };

    const handleCheckboxChange = (electionId) => {
        setSelectedElections((prev) =>
            prev.includes(electionId)
                ? prev.filter((id) => id !== electionId)
                : [...prev, electionId]
        );
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedElections([]);
        } else {
            setSelectedElections(elections.map((election) => election.id));
        }
        setSelectAll(!selectAll);
    };

    const handleAddElection = () => {
        const newElection = {
            id: (elections.length + 1).toString(),
            name: `Election ${elections.length + 1}`,
            description: "New description",
            candidateCount: 5,
            voterCount: 100,
            electionDate: new Date().toISOString().split("T")[0],
            active: false,
            completed: false,
        };
        setElections([...elections, newElection]);
        toast.success(`Election ${newElection.name} added successfully!`);
    };

    const handleStart = async (id) => {
        try {
            await startElection(id);
            toast.success("Election started");
            fetchElections();
        } catch (err) {
            console.error("Failed to start election:", err);
            toast.error("Failed to start election.");
        }
    };

    const handleEnd = async (id) => {
        try {
            await endElection(id);
            toast.success("Election ended");
            fetchElections();
        } catch (err) {
            console.error("Failed to end election:", err);
            toast.error("Failed to end election.");
        }
    };

    const activeElection = elections.find((e) => e.active);

    // Updated content class for better centering when sidebar is minimized
    const contentClass = isSidebarOpen
        ? "sm:ml-64 w-full sm:w-[calc(100%-16rem)]"
        : "ml-16 w-[calc(100%-4rem)] md:w-[calc(100%-4rem)] mx-auto flex justify-center";

    // Inner container class for centering content
    const innerContainerClass = !isSidebarOpen
        ? "w-full max-w-4xl"
        : "w-full";

    // Empty state component
    const EmptyState = () => (
        <div className="flex flex-col items-center justify-center p-4 md:p-10 text-center">
            <div className="bg-blue-50 rounded-full p-4 mb-4">
                <AlertCircle size={40} className="text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No Elections Found</h3>
            <p className="text-gray-600 mb-6 max-w-md">
                Create your first election to get started with managing your voting process.
            </p>
            <Link
                to="/RegisterElectionPage"
                className="bg-blue-500 text-white px-4 py-2 md:px-6 md:py-3 rounded-full flex items-center hover:bg-blue-600 transition shadow-md text-sm md:text-base"
            >
                Create New Election <CircleCheck className="ml-2" size={window.innerWidth < 768 ? 16 : 20} />
            </Link>
        </div>
    );

    return (
        <div style={{ fontFamily: "Montserrat, sans-serif" }} className="flex flex-col h-screen bg-bgColor relative">
            <TopBar>
                <button
                    onClick={toggleSidebar}
                    className="p-2 mr-2"
                    aria-label="Toggle sidebar"
                >
                    <Menu size={24} />
                </button>
            </TopBar>

            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className={`flex-1 transition-all duration-300 mt-16 ${contentClass}`}>
                <div className={`p-3 md:p-10 h-[calc(100vh-4rem)] overflow-auto ${innerContainerClass}`}>
                    {/* Top Header: Title (left) and empty container (right) */}
                    <div className="flex items-center justify-between mb-5">
                        <div className="mt-2 md:mt-5">
                            <div className="flex items-center space-x-2">
                                <h2 className="text-xl md:text-2xl font-bold">Elections</h2>
                                <div className="flex items-center text-lg md:text-2xl font-bold text-gray-600">
                                    <Users className="w-5 h-5 md:w-6 md:h-6 mr-1 stroke-[2]" />
                                    <span>{elections?.length || 0}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* List Container with Action Header */}
                    <div className="bg-white rounded-2xl shadow-lg w-full p-2 md:p-3">
                        {isLoading ? (
                            <div className="flex justify-center items-center p-10">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                            </div>
                        ) : elections.length > 0 ? (
                            <>
                                <div className="flex flex-col sm:flex-row justify-between items-center p-2 border-b space-y-2 sm:space-y-0">
                                    {/* Left: Select All control (only when selecting) */}
                                    <div className="flex items-center self-start sm:self-auto">
                                        {isSelecting && (
                                            <>
                                                <span className="text-xs md:text-sm mr-2">Select All</span>
                                                <div
                                                    className={`w-4 h-4 md:w-5 md:h-5 flex justify-center items-center rounded-full border-2 cursor-pointer ${
                                                        selectAll ? "bg-blue-500 border-blue-500 text-white" : "border-gray-400"
                                                    }`}
                                                    onClick={handleSelectAll}
                                                >
                                                    {selectAll && <CircleCheck size={16} className="text-white" />}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {/* Right: Action buttons */}
                                    <div className="flex items-center space-x-2 self-end sm:self-auto">
                                        <button
                                            onClick={handleSelectClick}
                                            className="text-blue-700 border border-blue-500 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-blue-100 hover:bg-blue-500 hover:text-white transition text-xs md:text-sm"
                                        >
                                            {isSelecting ? "Cancel" : "Select"}
                                        </button>
                                        {isSelecting && (
                                            <button
                                                onClick={handleDeleteSelectedDB}
                                                className="text-red-600 border border-red-500 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-red-100 hover:bg-red-500 hover:text-white transition flex items-center text-xs md:text-sm"
                                            >
                                                <Trash2 size={14} className="mr-1" /> Delete
                                            </button>
                                        )}
                                        <button className="text-gray-600 border border-gray-400 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-gray-200 hover:bg-gray-400 hover:text-white transition flex items-center text-xs md:text-sm">
                                            <SortAsc size={14} className="mr-1" />
                                        </button>
                                    </div>
                                </div>

                                {/* Elections List */}
                                <div className="overflow-y-auto max-h-[50vh] md:max-h-[500px] custom-scrollbar w-full space-y-3 mt-3">
                                    {elections.map((election) => {
                                        let actionSection = null;
                                        if (election.completed) {
                                            actionSection = (
                                                <div className="flex flex-col items-end">
                                                    <span className="text-gray-500 font-bold text-xs md:text-sm">Election Completed</span>
                                                    <button
                                                        onClick={() => navigate(`/VotingStatisticsUpdated?electionId=${election.id}`)}
                                                        className="mt-1 bg-blue-500 text-white px-2 py-1 md:px-3 md:py-1 rounded-md hover:bg-blue-600 text-xs md:text-sm"
                                                    >
                                                        View Statistics
                                                    </button>
                                                </div>
                                            );
                                        } else if (election.active) {
                                            actionSection = (
                                                <button
                                                    onClick={() => handleEnd(election.id)}
                                                    className="bg-red-500 text-white px-2 py-1 md:px-3 md:py-1 rounded-md hover:bg-red-600 text-xs md:text-sm"
                                                >
                                                    End
                                                </button>
                                            );
                                        } else {
                                            const disabled = !!activeElection;
                                            actionSection = (
                                                <button
                                                    onClick={() => handleStart(election.id)}
                                                    className={`bg-green-500 text-white px-2 py-1 md:px-3 md:py-1 rounded-md hover:bg-green-600 text-xs md:text-sm ${
                                                        disabled ? "opacity-50 cursor-not-allowed" : ""
                                                    }`}
                                                    disabled={disabled}
                                                >
                                                    Start
                                                </button>
                                            );
                                        }

                                        return (
                                            <div
                                                key={election.id}
                                                onDoubleClick={() => handleDoubleClick(election)}
                                                className="cursor-pointer bg-white rounded-md p-3 shadow hover:shadow-lg transition flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b"
                                            >
                                                <div className="flex items-start w-full sm:w-auto">
                                                    {isSelecting && (
                                                        <div
                                                            className={`w-4 h-4 md:w-5 md:h-5 flex justify-center items-center rounded-full border-2 cursor-pointer mr-2 md:mr-3 mt-1 ${
                                                                selectedElections.includes(election.id)
                                                                    ? "bg-blue-500 border-blue-500 text-white"
                                                                    : "border-gray-400"
                                                            }`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCheckboxChange(election.id);
                                                            }}
                                                        >
                                                            {selectedElections.includes(election.id) && (
                                                                <CircleCheck size={16} className="text-white" />
                                                            )}
                                                        </div>
                                                    )}
                                                    <ElectionList
                                                        name={election.name}
                                                        description={election.description}
                                                        candidateCount={election.candidateCount}
                                                        voterCount={election.voterCount}
                                                        electionDate={election.electionDate}
                                                    />
                                                </div>
                                                <div className="self-end sm:self-center">{actionSection}</div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Bottom Action Buttons */}
                                <div className="flex flex-col sm:flex-row items-center justify-between mt-4 space-y-3 sm:space-y-0 px-2">
                                    <Link to="/RegisterElectionPage" onClick={handleAddElection} className="text-blue-500 text-xs md:text-sm self-start">
                                        + Add Election
                                    </Link>
                                </div>
                            </>
                        ) : (
                            <EmptyState />
                        )}
                    </div>
                </div>
            </div>
            <BottomBar />
            <Toaster />
        </div>
    );
};

export default Elections;